/**
 * src/utils/migrateModalities.js
 *
 * One-time migration utility: converts old flat modality category arrays
 * to the new two-tier structured format used by ModalitySelector.
 *
 * OLD FORMAT (9-tile checkbox grid from pre-taxonomy onboarding):
 *   modalities: ["Divination", "Energy Healing", "Other"]
 *
 * NEW FORMAT (two-tier taxonomy):
 *   modalities: {
 *     selected: [{ id, name, parentId, parentName }],
 *     custom:   [{ name, addedAt }],
 *     parentCategories: ['divination', 'energy']
 *   }
 *
 * Mapping: old category label → parent category in the new taxonomy.
 * Because old categories were broad (parent-level), each maps to a
 * "{parentId}-general" entry in `selected`. The practitioner can later
 * replace this placeholder with specific modalities via the ModalitySelector.
 *
 * "Other" (and any unrecognised string) is preserved as a custom entry.
 */

const OLD_CATEGORY_MAP = {
  'Divination':                  { parentId: 'divination', parentName: 'Divination & Intuitive Arts' },
  'Energy Healing':              { parentId: 'energy',     parentName: 'Energy Work & Healing' },
  'Mediumship & Channeling':     { parentId: 'mediumship', parentName: 'Mediumship & Spirit Communication' },
  'Psychic & Intuitive Readings':{ parentId: 'divination', parentName: 'Divination & Intuitive Arts' },
  'Astrology & Numerology':      { parentId: 'astrology',  parentName: 'Astrology & Chart-Based Practices' },
  'Coaching & Guidance':         { parentId: 'coaching',   parentName: 'Coaching & Guidance' },
  'Bodywork & Somatic':          { parentId: 'mind-body',  parentName: 'Mind-Body Practices' },
  'Herbalism & Plant Medicine':  { parentId: 'herbalism',  parentName: 'Herbalism & Plant Medicine' },
};

/**
 * Returns true when `modalities` is in the old flat-array format.
 * Distinguishes it from the new structured object format.
 *
 * @param {any} modalities
 * @returns {boolean}
 */
export const isOldModalityFormat = (modalities) => {
  return Array.isArray(modalities);
};

/**
 * Converts an old flat modality array to the new structured format.
 *
 * - Known category strings → `selected` as a "{parentId}-general" placeholder entry
 * - Duplicate parent categories are collapsed (e.g. "Divination" + "Psychic & Intuitive
 *   Readings" both map to divination — only one general entry is created)
 * - "Other" and any unrecognised strings → `custom` entries (preserved, not discarded)
 * - Empty or invalid input → returns empty structured object
 *
 * @param {string[]} oldModalities - Old flat array, e.g. ["Divination", "Other"]
 * @returns {{ selected: Array, custom: Array, parentCategories: string[] }}
 */
export const migrateModalities = (oldModalities) => {
  if (!Array.isArray(oldModalities) || oldModalities.length === 0) {
    return { selected: [], custom: [], parentCategories: [] };
  }

  const selected = [];
  const custom = [];
  const seenParentIds = new Set();

  for (const entry of oldModalities) {
    if (typeof entry !== 'string') continue;

    const mapping = OLD_CATEGORY_MAP[entry];

    if (mapping) {
      // Collapse duplicates that map to the same parent (e.g. "Divination" + "Psychic & Intuitive Readings")
      if (!seenParentIds.has(mapping.parentId)) {
        seenParentIds.add(mapping.parentId);
        selected.push({
          id: `${mapping.parentId}-general`,
          name: `${mapping.parentName} (General)`,
          parentId: mapping.parentId,
          parentName: mapping.parentName,
        });
      }
    } else {
      // "Other" or any unrecognised string — preserve as custom entry
      custom.push({
        name: entry,
        addedAt: new Date().toISOString(),
      });
    }
  }

  const parentCategories = [...seenParentIds];

  console.log(
    `🔄 MIGRATE MODALITIES: [${oldModalities.join(', ')}] → ` +
    `${selected.length} selected (${parentCategories.join(', ')}), ` +
    `${custom.length} custom`
  );

  return { selected, custom, parentCategories };
};
